import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { ListItemDto, ListPayloadDto, ListResponseDto } from '../../dto';
import { ListTypes } from 'src/app/core/models/savedsearches-lists.model';
import { GET_LIST_LOADING_TAG, SI_VIRTUAL_OPTION_ITEM_SIZE_PX } from '../../constants';
import { ListApiService } from '../../services/api';
import { CacheService, LoadingService } from '../../services';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Lists } from '../../services/lists.service';

@UntilDestroy()
@Component({
    selector: 'app-existing-list-selector',
    templateUrl: './existing-list-selector.component.html',
    standalone: false
})
export class ExistingListSelectorComponent implements OnInit {
    @Input() canSelectNullableOptions: boolean = true;
    @Input() selectedType: 'existingContactList' | 'existingCompanyList' | 'suppressionList';
    @Input() label: string = 'Select from list';
    @Input() placeholder: string = 'Select from list';
    @Input() showEmptyOption: boolean = true;
    @Input() loadingTag: string;
    @Input() cacheable: boolean;
    @Input() cacheTag: string;
    @Output() selectionChanged = new EventEmitter<number>();
    @ViewChild(CdkVirtualScrollViewport) viewPort: CdkVirtualScrollViewport;

    isGetListLoading$: Observable<boolean>;

    lists: ListItemDto[] = [];
    readonly itemsSize: number = SI_VIRTUAL_OPTION_ITEM_SIZE_PX;
    private listPayload: ListPayloadDto = {
        page: 1,
        sortBy: 'Name',
        sortDirection: 'asc',
        listTypes: Object.keys(ListTypes),
        isPublic: true
    };
    private isListLoaded: boolean = false;
    private listLength: number = 0;
    private listPageSize: number = 0;
    private readonly scrollEventSubject = new Subject<void>();

    constructor(
        private readonly listApiService: ListApiService,
        private readonly loadingService: LoadingService,
        private readonly listsService: Lists,
        private readonly cacheService: CacheService,
    ) { }

    ngOnInit(): void {
        this.scrollEventSubject.pipe(debounceTime(200), untilDestroyed(this)).subscribe(() => {
            this.checkScrollPosition();
        });
        this.isGetListLoading$ = this.loadingService.getIsLoading(this.loadingTag || GET_LIST_LOADING_TAG);
    }

    onPanelOpen(): void {
        switch (this.selectedType) {
            case 'existingContactList':
                this.listPayload = { ...this.listPayload, listTypes: ['Contacts'] };
                break;

            case 'existingCompanyList':
                this.listPayload = { ...this.listPayload, listTypes: ['Companies'] };
                break;

            case 'suppressionList':
                this.listPayload = { ...this.listPayload, listTypes: ['Suppression'] };
                break;
        }
        if (!this.isListLoaded) {
            this.getListData();
        } else {
            this.viewPort.checkViewportSize();
        }
    }

    onVirtualScroll(): void {
        this.scrollEventSubject.next();
    }

    getListData(loadMore: boolean = false): void {
        this.getList(this.listPayload).pipe(untilDestroyed(this)).subscribe((resp: ListResponseDto) => {
            if (resp.page === 1) {
                this.listPayload = { ...this.listPayload, page: resp.page };
                this.listLength = resp.totalCount;
                this.listPageSize = resp.pageSize;
            }
            const decoded = this.listsService.getListRawData(resp.items);
            this.lists = [...this.lists, ...decoded];
            if (!loadMore) {
                this.lists = [...decoded];
            }
            this.isListLoaded = true;
        });
    }

    onSelectionChange(listId: number) {
        this.selectionChanged.emit(listId)
    }

    private checkScrollPosition(): void {
        const offsetBottom = this.viewPort.measureScrollOffset('bottom');
        const offsetTop = this.viewPort.measureScrollOffset('top');
        if (offsetTop !== 0 && offsetBottom < 200 && !this.isAllRecordLoaded()) {
            this.listPayload = { ...this.listPayload, page: this.listPayload.page + 1 };
            this.getListData(true);
        }
    }

    private isAllRecordLoaded(): boolean {
        return this.listLength <= (this.listPayload.page * this.listPageSize);
    }

    private getList(listPayload: ListPayloadDto): Observable<ListResponseDto> {
        const loadingTag: string = this.loadingTag || GET_LIST_LOADING_TAG;
        const cacheTag: string = this.cacheTag || this.loadingTag || GET_LIST_LOADING_TAG;
        if (this.cacheable) {
            return this.loadingService.loadingWrapper(
              this.cacheService.getCachedObservable(cacheTag, this.listApiService.getList(listPayload)),
              loadingTag
            );
        }
        return this.loadingService.loadingWrapper(this.listApiService.getList(listPayload), loadingTag);
    }
}
